// MARK://dhk8/pos/features/reports/report-detail-page
// Renders one of the management reports: ventas-por-dia, productos-mas-vendidos,
// propinas-por-empleado. Date-range picker + CSV export button. Tables use the
// DataTable mobile/compact variant (works at 534×738 portrait).
//
// Data: aggregated client-side from dhk8-pos-orders / dhk8-pos-tips (real clients),
// falling back to deterministic seed data in dev (VITE_REPORTS_STUB). The bigger
// pre-aggregated reports / CSV export endpoint belongs to dhk8-erp-reports — see
// clients/reports.ts TODO backend.

import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { DataTable, type ColumnDef } from '../../components/DataTable.js'
import {
  REPORT_CATALOG,
  getDailySales,
  getTopProducts,
  getTipsByEmployee,
  rowsToCsv,
  downloadCsv,
  reportsIsoDaysAgo,
  type ReportId,
  type DailySalesPoint,
  type TopProductRow,
  type TipsByEmployeeRow,
} from '../../lib/clients/reports.js'
import { useAuthStore } from '../../stores/auth-store.js'
import { formatCents } from '../../lib/money.js'
import { reportsStyles } from './reports-styles.js'

type AnyRow = DailySalesPoint | TopProductRow | TipsByEmployeeRow

function isReportId(s: string | undefined): s is ReportId {
  return s === 'ventas-por-dia' || s === 'productos-mas-vendidos' || s === 'propinas-por-empleado'
}

export default function ReportDetailPage() {
  const { reportId } = useParams<{ reportId: string }>()
  const navigate = useNavigate()
  const branchId = useAuthStore((s) => s.user?.branchId)

  const valid = isReportId(reportId)
  const meta = valid ? REPORT_CATALOG.find((r) => r.id === reportId)! : null

  const [from, setFrom] = useState(reportsIsoDaysAgo(7))
  const [to, setTo] = useState(reportsIsoDaysAgo(0))
  const [rows, setRows] = useState<AnyRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!valid) return
    const ctrl = new AbortController()
    // Guard contra updates tras unmount/abort: si el effect se limpia antes de que
    // resuelva el fetch, no tocamos estado (evita el error "after teardown" en tests
    // y el warning update-on-unmounted en prod). Ref: F7.1 CI gate.
    let alive = true
    setLoading(true)
    setError(null)
    const range = { from: `${from}T00:00:00.000Z`, to: `${to}T23:59:59.999Z`, ...(branchId ? { branchId } : {}) }
    const p =
      reportId === 'ventas-por-dia'
        ? getDailySales(range, ctrl.signal)
        : reportId === 'productos-mas-vendidos'
          ? getTopProducts(range, 50, ctrl.signal)
          : getTipsByEmployee(range, ctrl.signal)
    p.then((r) => { if (alive) setRows(r as AnyRow[]) })
      .catch((e) => { if (alive) setError(e instanceof Error ? e.message : 'No se pudo cargar el reporte') })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false; ctrl.abort() }
  }, [valid, reportId, from, to, branchId])

  const { columns, renderCell, getRowKey, primaryKey, kpis, csv } = useMemo(() => {
    if (reportId === 'ventas-por-dia') {
      const data = rows as DailySalesPoint[]
      const totalSales = data.reduce((s, r) => s + r.salesCents, 0)
      const totalOrders = data.reduce((s, r) => s + r.ordersCount, 0)
      const cols: ColumnDef<DailySalesPoint>[] = [
        { key: 'date', label: 'Fecha', sortable: true, sortValue: (r) => r.date },
        { key: 'ordersCount', label: 'Órdenes', numeric: true, align: 'end', sortable: true, sortValue: (r) => r.ordersCount, hideOnNarrow: true },
        { key: 'salesCents', label: 'Ventas', numeric: true, align: 'end', sortable: true, sortValue: (r) => r.salesCents },
        { key: 'avgTicketCents', label: 'Ticket prom.', numeric: true, align: 'end', hideOnNarrow: true },
      ]
      return {
        columns: cols as unknown as ColumnDef<AnyRow>[],
        renderCell: ((r: DailySalesPoint, c: ColumnDef<DailySalesPoint>) =>
          c.key === 'date'
            ? new Date(`${r.date}T00:00:00`).toLocaleDateString('es-MX', { weekday: 'short', day: '2-digit', month: 'short' })
            : c.key === 'ordersCount'
              ? String(r.ordersCount)
              : c.key === 'salesCents'
                ? <span className="dhk8-rpt__money">{formatCents(r.salesCents)}</span>
                : <span className="dhk8-rpt__money">{formatCents(r.avgTicketCents)}</span>) as (r: AnyRow, c: ColumnDef<AnyRow>) => React.ReactNode,
        getRowKey: ((r: DailySalesPoint) => r.date) as (r: AnyRow) => string,
        primaryKey: 'date',
        kpis: [
          { l: 'Días con ventas', v: String(data.length) },
          { l: 'Total vendido', v: formatCents(totalSales) },
          { l: 'Órdenes', v: String(totalOrders) },
          { l: 'Ticket promedio', v: formatCents(totalOrders > 0 ? Math.round(totalSales / totalOrders) : 0) },
        ],
        csv: () => rowsToCsv(['Fecha', 'Órdenes', 'Ventas (centavos)', 'Ticket promedio (centavos)'], data.map((r) => [r.date, r.ordersCount, r.salesCents, r.avgTicketCents])),
      }
    }
    if (reportId === 'productos-mas-vendidos') {
      const data = rows as TopProductRow[]
      const totalUnits = data.reduce((s, r) => s + r.quantity, 0)
      const totalCents = data.reduce((s, r) => s + r.totalCents, 0)
      const cols: ColumnDef<TopProductRow>[] = [
        { key: 'rank', label: '#', align: 'end' },
        { key: 'name', label: 'Producto', sortable: true, sortValue: (r) => r.name.toLowerCase() },
        { key: 'quantity', label: 'Unidades', numeric: true, align: 'end', sortable: true, sortValue: (r) => r.quantity, hideOnNarrow: true },
        { key: 'totalCents', label: 'Importe', numeric: true, align: 'end', sortable: true, sortValue: (r) => r.totalCents },
      ]
      return {
        columns: cols as unknown as ColumnDef<AnyRow>[],
        renderCell: ((r: TopProductRow, c: ColumnDef<TopProductRow>, idx: number) =>
          c.key === 'rank'
            ? String(idx + 1)
            : c.key === 'name'
              ? r.name
              : c.key === 'quantity'
                ? String(r.quantity)
                : <span className="dhk8-rpt__money">{formatCents(r.totalCents)}</span>) as (r: AnyRow, c: ColumnDef<AnyRow>, idx: number) => React.ReactNode,
        getRowKey: ((r: TopProductRow) => r.productId) as (r: AnyRow) => string,
        primaryKey: 'name',
        kpis: [
          { l: 'Productos distintos', v: String(data.length) },
          { l: 'Unidades vendidas', v: String(totalUnits) },
          { l: 'Importe total', v: formatCents(totalCents) },
        ],
        csv: () => rowsToCsv(['#', 'Producto', 'Unidades', 'Importe (centavos)'], data.map((r, i) => [i + 1, r.name, r.quantity, r.totalCents])),
      }
    }
    // propinas-por-empleado
    const data = rows as TipsByEmployeeRow[]
    const totalPooled = data.reduce((s, r) => s + r.pooledCents, 0)
    const totalDirect = data.reduce((s, r) => s + r.directCents, 0)
    const cols: ColumnDef<TipsByEmployeeRow>[] = [
      { key: 'employeeName', label: 'Empleado', sortable: true, sortValue: (r) => r.employeeName.toLowerCase() },
      { key: 'pooledCents', label: 'Pool', numeric: true, align: 'end', sortable: true, sortValue: (r) => r.pooledCents, hideOnNarrow: true },
      { key: 'directCents', label: 'Directas', numeric: true, align: 'end', hideOnNarrow: true },
      { key: 'totalCents', label: 'Total', numeric: true, align: 'end', sortable: true, sortValue: (r) => r.totalCents },
    ]
    return {
      columns: cols as unknown as ColumnDef<AnyRow>[],
      renderCell: ((r: TipsByEmployeeRow, c: ColumnDef<TipsByEmployeeRow>) =>
        c.key === 'employeeName'
          ? r.employeeName
          : c.key === 'pooledCents'
            ? <span className="dhk8-rpt__money">{formatCents(r.pooledCents)}</span>
            : c.key === 'directCents'
              ? <span className="dhk8-rpt__money">{formatCents(r.directCents)}</span>
              : <span className="dhk8-rpt__money">{formatCents(r.totalCents)}</span>) as (r: AnyRow, c: ColumnDef<AnyRow>) => React.ReactNode,
      getRowKey: ((r: TipsByEmployeeRow) => r.employeeId) as (r: AnyRow) => string,
      primaryKey: 'employeeName',
      kpis: [
        { l: 'Empleados', v: String(data.length) },
        { l: 'Pool repartido', v: formatCents(totalPooled) },
        { l: 'Propinas directas', v: formatCents(totalDirect) },
        { l: 'Total propinas', v: formatCents(totalPooled + totalDirect) },
      ],
      csv: () => rowsToCsv(['Empleado', 'Pool (centavos)', 'Directas (centavos)', 'Total (centavos)'], data.map((r) => [r.employeeName, r.pooledCents, r.directCents, r.totalCents])),
    }
  }, [reportId, rows])

  function exportCsv() {
    const text = csv()
    downloadCsv(`${reportId ?? 'reporte'}_${from}_${to}.csv`, text)
  }

  if (!valid) {
    return (
      <div className="dhk8-rpt">
        <button className="dhk8-rpt__back" onClick={() => navigate('/reports/hub')}>‹ Volver a reportes</button>
        <div className="dhk8-rpt__notice">Reporte no encontrado.</div>
        <style>{reportsStyles}</style>
      </div>
    )
  }

  return (
    <div className="dhk8-rpt">
      <button className="dhk8-rpt__back" onClick={() => navigate('/reports/hub')}>‹ Volver a reportes</button>
      <header className="dhk8-rpt__head">
        <h1 className="dhk8-rpt__title">{meta!.name}</h1>
        <p className="dhk8-rpt__sub">{meta!.description}</p>
      </header>

      <div className="dhk8-rpt__toolbar">
        <label>Desde<input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label>Hasta<input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
        <button className="dhk8-rpt__btn" onClick={exportCsv} disabled={loading || rows.length === 0}>Exportar CSV</button>
      </div>

      {error && <div className="dhk8-rpt__error">{error}</div>}

      <div className="dhk8-rpt__kpis">
        {kpis.map((k) => (
          <div key={k.l} className="dhk8-rpt__kpi"><div className="dhk8-rpt__kpi-l">{k.l}</div><div className="dhk8-rpt__kpi-v">{k.v}</div></div>
        ))}
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        getRowKey={getRowKey}
        renderCell={renderCell}
        loading={loading}
        pageSize={25}
        primaryKey={primaryKey}
        ariaLabel={meta!.name}
        emptyState={<span>Sin datos en el periodo seleccionado.</span>}
      />

      <style>{reportsStyles}</style>
    </div>
  )
}
